import {
  ModalRef,
  useEntityMetadata,
  useModuleFederationContext,
  useTranslation,
  useViewBaseMetadata,
} from '@msdk/common'
import {
  EntityMetadata,
  EntityModel,
  GenericFieldValue,
  readEntityModelIncludeExternal,
  ViewModel,
} from '@msdk/definitions'
import { ButtonIcon, CloseIcon, FormHeaderWrapper, Tabs, TabsContainer } from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { useSitemapViews } from '../../hooks'
import { resolveItem } from '../../utils'
import { ImportedComponentErrorBoundary } from '../ImportedComponentBoundary'
import { useViewContext, ViewContextProvider } from '../ViewContextProvider'
import { ViewHeader } from '../ViewHeader'
import { SignleModalView } from './SingleModalView'

interface Props {
  entityName: string
  viewName: string
  modalRef?: ModalRef
  lookup: boolean
  multiSelect?: boolean
  selectedRowIds?: string[]
  prefilledFilters?: Record<string, GenericFieldValue>
}

function ModalViewContent(props: Props) {
  const views = props.viewName.split(',') as string[]
  const pairs = useSitemapViews(props.entityName, props.viewName)

  const model = useEntityMetadata(props.entityName) as EntityModel
  const viewModel = model.getView(props.viewName)

  const baseMetadata = useViewBaseMetadata(props.entityName, views[0]) as EntityMetadata
  const modules = useModuleFederationContext()
  const msModule = modules.loadedModules[baseMetadata.msProvider]
  const ImportedComponent = resolveItem(msModule, viewModel?.componentDefinition?.fullName)
  const t = useTranslation()
  const [currentTab, setCurrentTab] = useState<number>(0)

  const getViewLabel = (entityName: string, viewName: string) => {
    const entityModel = readEntityModelIncludeExternal(entityName, baseMetadata.msProvider) as EntityModel
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const viewModel = entityModel.getView(viewName) as ViewModel
    return t(viewModel.idml, viewModel.description)
  }

  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const viewContext = useViewContext()

  const onTabChange = (newValue: number) => {
    setIsInitialized(false)
    setCurrentTab(newValue)
  }

  useEffect(() => {
    if (viewContext.context.isInitialized) {
      setIsInitialized(true)
    }
  }, [viewContext])

  return (
    <>
      {pairs.length === 1 && ImportedComponent && (
        <ImportedComponentErrorBoundary>
          <ImportedComponent />
        </ImportedComponentErrorBoundary>
      )}
      {pairs.length === 1 && !ImportedComponent && (
        <>
          <FormHeaderWrapper $isModal={true}>
            <ViewHeader
              entityName={props.entityName}
              viewName={props.viewName}
              mode="Modal"
              isInitialized={isInitialized}
            />
            <ButtonIcon onClick={() => props.modalRef?.close()}>
              <CloseIcon />
            </ButtonIcon>
          </FormHeaderWrapper>
          <SignleModalView {...props} />
        </>
      )}
      {pairs.length > 1 && (
        <>
          <FormHeaderWrapper $isModal={true}>
            <ViewHeader
              entityName={pairs[currentTab].entityName}
              viewName={pairs[currentTab].viewName}
              mode="Modal"
              isInitialized={isInitialized}
            />
            <ButtonIcon onClick={() => props.modalRef?.close()}>
              <CloseIcon />
            </ButtonIcon>
          </FormHeaderWrapper>
          <TabsContainer className="modal-tabs-content">
            <Tabs
              onChange={onTabChange}
              childs={pairs.map((p) => {
                return {
                  label: getViewLabel(p.entityName, p.viewName),
                  content: <SignleModalView {...props} entityName={p.entityName} viewName={p.viewName} />,
                }
              })}
            ></Tabs>
          </TabsContainer>
        </>
      )}
    </>
  )
}

export function ModalView(props: Props) {
  return (
    <ViewContextProvider>
      <ModalViewContent {...props} />
    </ViewContextProvider>
  )
}
